import { Link } from "react-router-dom";
import {
  Play,
  Building2,
  Truck,
  Wrench,
  Package,
  Cog,
  ArrowRight,
  CheckCircle2,
  Globe2,
  Sparkles,
  Plane,
  Gift,
  ShieldCheck,
  Ticket,
} from "lucide-react";

const CAPABILITIES = [
  {
    icon: Building2,
    title: "Commercial Real Estate",
    desc: "We scout, vet, and secure offices, retail storefronts, and industrial warehouse spaces tailored to your operational needs.",
  },
  {
    icon: Wrench,
    title: "Turnkey Solutions",
    desc: "We deliver fully functional, move-in-ready business setups, eliminating the stress of operational logistics.",
  },
  {
    icon: Cog,
    title: "Property Upkeep & Management",
    desc: "We source and manage reliable maintenance crews, structural materials, and specialized trade contractors.",
  },
  {
    icon: Package,
    title: "Wholesale Assets & Inventory",
    desc: "We buy premium corporate liquidation stock and asset-company inventory, passing deep wholesale discounts directly to you.",
  },
  {
    icon: Truck,
    title: "Everyday Business Equipment",
    desc: "We procure, restock, or liquidate machinery, hardware, raw supplies, and physical consumer goods on demand.",
  },
];

const PILLARS = [
  {
    title: "Absolute Procurement Coverage",
    desc: "Eliminate the cost of multiple agents. You deal with one point of contact for everything imaginable.",
  },
  {
    title: "Direct-to-Cost Savings",
    desc: "Access elite liquidation assets at raw wholesale prices that are unavailable to the general public.",
  },
  {
    title: "Speed to Market",
    desc: "Our deeply rooted industry connections allow us to find rare physical assets and premium properties faster than standard brokers.",
  },
];

const DEPLOY_USE_CASES = [
  {
    title: "For Realtors & Property Managers",
    desc: "Hand a luxury vacation voucher to prospective tenants or buyers who walk through your door. Lock in leases instantly and out-pace local competitors.",
  },
  {
    title: "For Manufacturers & Industrial Businesses",
    desc: "Erase your corporate hospitality budget. Reward your top team members with premium vacations at zero extra cost to your bottom line.",
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden aios-starfield">
        <div className="absolute inset-0 aios-grid-overlay opacity-40 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/40 via-transparent to-[#020617] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 aios-fade-up">
              <div className="inline-flex items-center gap-2 border border-red-600/40 bg-red-600/10 px-4 py-2 mb-6">
                <Sparkles className="w-4 h-4 text-red-500" />
                <span className="font-display text-xs tracking-[0.25em] text-red-400 font-bold uppercase">
                  Aggressive Corporate Sourcing · A.I.O
                </span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]">
                Wipe Out Moving Concessions &amp;{" "}
                <span className="text-red-500">Corporate Incentive Overhead.</span>
              </h1>

              <p className="mt-8 text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl">
                Deploy premium 4-to-5-day luxury resort room certificates for your tenants, clients, or employees.
                <span className="text-white font-semibold"> Baseline room rates are covered in full;</span> travelers simply cover local nightly tax recovery fees and resort overhead ranging dynamically from <span className="text-red-400 font-semibold">$34 to $89 per night</span>.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-primary-cta"
                  className="aios-btn-primary text-base aios-pulse"
                >
                  [ SUBMIT A SOURCING REQUEST NOW ]
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>

              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-red-500" /> One Vendor</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-red-500" /> One Invoice</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-red-500" /> Corporate-Tier Pricing</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-red-500" /> Nationwide Coverage</div>
                <div className="flex items-center gap-2" data-testid="hero-fully-insured"><ShieldCheck className="w-4 h-4 text-red-500" /> Fully Insured</div>
              </div>
            </div>

            {/* Hero Video */}
            <div className="lg:col-span-5 aios-fade-up" style={{ animationDelay: "0.2s" }}>
              <div
                data-testid="hero-video-container"
                className="relative aspect-video bg-[#0B1221] border border-slate-700 group overflow-hidden"
              >
                <video
                  data-testid="hero-video"
                  src="/assets/hero-sourcing.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-red-600 px-3 py-1 font-display text-[10px] tracking-[0.25em] text-white font-bold pointer-events-none">
                  ● A.I.O SOURCING REEL
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-500 font-display tracking-widest uppercase text-center">
                Inside the Sourcing Hub
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CAPABILITIES GRID ===== */}
      <section className="relative py-24 lg:py-32 bg-[#0B1221]" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
              OUR CORE SOURCING CAPABILITIES
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 tracking-tighter">
              If your business needs it, <br />
              <span className="text-red-500">we source it.</span>
            </h2>
            <p className="mt-6 text-slate-400 text-lg leading-relaxed">
              Our comprehensive networks allow us to manage procurement across multiple major industries simultaneously.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CAPABILITIES.map((cap, idx) => {
              const Icon = cap.icon;
              return (
                <div
                  key={cap.title}
                  data-testid={`capability-card-${idx}`}
                  className="aios-card p-8 lg:p-10"
                >
                  <div className="aios-card-accent" />
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-red-600/10 border border-red-600/30">
                      <Icon className="w-6 h-6 text-red-500" />
                    </div>
                    <div className="font-display text-slate-500 text-xs tracking-widest font-bold mt-3">
                      0{idx + 1}
                    </div>
                  </div>
                  <h3 className="font-display text-xl lg:text-2xl font-bold text-white mt-6 tracking-tight">
                    {cap.title}
                  </h3>
                  <p className="mt-4 text-slate-400 leading-relaxed">{cap.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== POWER OF ONE VENDOR ===== */}
      <section className="relative py-24 lg:py-32 bg-[#020617]" data-testid="power-of-one-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div
                className="relative aspect-[4/5] overflow-hidden border border-slate-800"
                data-testid="power-of-one-image"
              >
                <img
                  src="https://images.unsplash.com/photo-1494412519320-aa613dfb7738?w=900&q=80"
                  alt="Global logistics and sourcing operations"
                  className="w-full h-full object-cover grayscale contrast-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-center gap-3">
                    <Globe2 className="w-6 h-6 text-red-500" />
                    <span className="font-display text-white text-sm tracking-[0.25em] uppercase font-bold">
                      Global Network · One Roof
                    </span>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 bg-red-600 px-6 py-4 font-display font-bold text-white text-sm tracking-widest uppercase hidden lg:block">
                One Vendor.
              </div>
            </div>

            <div>
              <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
                THE POWER OF ONE VENDOR
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 tracking-tighter">
                Why fragmenting your sourcing is{" "}
                <span className="text-red-500">costing you money</span>.
              </h2>

              <div className="mt-10 space-y-6">
                {PILLARS.map((pillar, idx) => (
                  <div
                    key={idx}
                    className="flex gap-5 border-l-2 border-red-600 pl-6 py-2"
                    data-testid={`power-pillar-${idx}`}
                  >
                    <div>
                      <h3 className="font-display font-bold text-white text-lg lg:text-xl tracking-tight">
                        {pillar.title}
                      </h3>
                      <p className="mt-2 text-slate-400 leading-relaxed">{pillar.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/activate" data-testid="power-cta-activate" className="aios-btn-primary text-sm">
                  Activate Portal · $485
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" data-testid="power-cta-contact" className="aios-btn-secondary text-sm">
                  Submit Sourcing Request
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ULTIMATE CORPORATE INCENTIVE ===== */}
      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="corporate-incentive-section">
        <div className="absolute inset-0 aios-starfield" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/70 via-[#0B1221]/80 to-[#020617]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
              THE ULTIMATE CORPORATE INCENTIVE
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 tracking-tighter">
              Wipe Out Corporate Travel Costs &{" "}
              <span className="text-red-500">Close Deals Faster.</span>
            </h2>
            <p className="mt-6 text-slate-300 text-lg leading-relaxed">
              When you partner with our sourcing firm, your $485 corporate license unlocks a powerful tool to drive business growth.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 items-stretch">
            <div className="lg:col-span-7 aios-card p-10 lg:p-12">
              <div className="aios-card-accent" />
              <div className="flex items-center gap-3">
                <Gift className="w-7 h-7 text-red-500" />
                <span className="font-display text-xs tracking-[0.25em] text-red-400 font-bold uppercase">
                  Included With Activation
                </span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-extrabold text-white mt-4 tracking-tighter">
                Get Unlimited 4-to-5 Day <br />
                <span className="text-red-500">Luxury Hotel Vouchers.</span>
              </h3>
              <p className="mt-5 text-slate-300 leading-relaxed">
                Use them as strategic business assets to lock in leases, reward high-performing employees, or entertain high-value clients.{" "}
                <span className="text-white font-semibold">Zero timeshare traps. Zero hidden fees for your business. One flat price.</span>
              </p>

              <div className="mt-8 space-y-5">
                {DEPLOY_USE_CASES.map((u, idx) => (
                  <div key={idx} className="border-l-2 border-red-600 pl-5 py-1" data-testid={`deploy-usecase-${idx}`}>
                    <h4 className="font-display font-bold text-white text-base lg:text-lg tracking-tight">{u.title}</h4>
                    <p className="text-slate-400 mt-1 leading-relaxed">{u.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-[#020617]/70 border border-slate-800 text-sm text-slate-400">
                <span className="font-bold text-slate-300">Note:</span> Your subscription covers the baseline luxury room rate entirely.
                The individual traveler is only responsible for standard local nightly government hotel taxes and resort fees at activation.
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/activate" data-testid="incentive-cta-activate" className="aios-btn-primary text-sm">
                  [ ACTIVATE YOUR LICENSED PORTAL NOW ]
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/destinations" data-testid="incentive-cta-destinations" className="aios-btn-secondary text-sm">
                  [ VIEW THE 130+ DESTINATIONS ]
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col gap-6">
              <div className="aios-card p-8 flex-1">
                <div className="aios-card-accent" />
                <Plane className="w-7 h-7 text-red-500" />
                <h4 className="font-display font-bold text-white text-xl tracking-tight mt-4">
                  130+ Luxury Destinations Worldwide
                </h4>
                <p className="text-slate-400 mt-3 leading-relaxed">
                  Pre-vetted 4 &amp; 5-star properties across major business hubs and leisure capitals — bookable on-demand through your portal.
                </p>
              </div>

              <div className="aios-card p-8 flex-1">
                <div className="aios-card-accent" />
                <ShieldCheck className="w-7 h-7 text-red-500" />
                <h4 className="font-display font-bold text-white text-xl tracking-tight mt-4">
                  The Sourcing Proof Guarantee
                </h4>
                <p className="text-slate-400 mt-3 leading-relaxed">
                  A single night at a standard 4-star hotel costs more than $485. By generating just one voucher for a client, tenant, or employee,
                  your corporate license has already paid for itself.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW THE TRAVEL INCENTIVE WORKS — 4 CARD GRID ===== */}
      <section className="relative py-24 lg:py-28 bg-[#0B1221] border-y border-slate-800" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">HOW THE TRAVEL INCENTIVE WORKS</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 tracking-tighter">
              The exact fulfillment rules <br />
              <span className="text-red-500">— transparent and enforceable.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "No Immediate Dates Needed", sub: "18-Month Travel Window", body: "Advancing travel dates is not required at the time of initial voucher receipt. Recipients have a full 18-month period from the date of issuance to select travel dates, log in, and complete their premium stay." },
              { title: "7-Day Activation Requirement", sub: "Rapid 7-Day Activation", body: "To secure the room allocation in the reservation engine, the recipient must activate their certificate within seven (7) days of receiving it by simply paying our recovery costs for the local government taxes and tourism fees." },
              { title: "Couple's Getaway Focus", sub: "Designed for 2 Adults", body: "Each vacation incentive is structurally configured as a couple's getaway. The majority of available room types will accommodate up to two (2) adults. Some destinations do not feature room setups for children, and adding children may reduce your hotel options or available date selections." },
              { title: "Strict Group Travel Ban", sub: "Group Travel is Strictly Prohibited", body: "Only one (1) party among friends, family, acquaintances, or known groups can use a promotional certificate for the same or overlapping travel dates. Consecutive voucher chaining to extend a single stay is blocked by backend network logic." },
            ].map((c, i) => (
              <div key={i} data-testid={`how-card-${i}`} className="aios-card p-7">
                <div className="aios-card-accent" />
                <div className="font-display text-red-500 text-[10px] tracking-[0.3em] font-bold uppercase mb-2">RULE 0{i + 1}</div>
                <h3 className="font-display text-lg font-extrabold text-white tracking-tight">{c.title}</h3>
                <div className="text-red-400 text-xs font-semibold mt-1">{c.sub}</div>
                <p className="mt-4 text-slate-400 text-sm leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>

          {/* BOOKING ENGINE RULE SHEET */}
          <div className="mt-14 aios-card p-8 lg:p-10" data-testid="booking-rules-block">
            <div className="aios-card-accent" />
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="w-6 h-6 text-red-500" />
              <span className="font-display text-xs tracking-[0.3em] font-bold text-red-500 uppercase">Booking Optimization Rules</span>
            </div>
            <p className="text-slate-300 leading-relaxed">
              For the best hotel/resort options and maximum inventory availability, travelers should search for <span className="text-white font-semibold">weekday travel blocks featuring a check-in from Sunday through Thursday</span>. Some weekend dates may feature limited availability or carry small resort surcharges. Certain high-traffic national holidays and special events may have limited or no availability blocks; keep in mind you have a <span className="text-white font-semibold">full 18-month execution period</span> to select amazing options. Once activated, check your email for a secure link to manage your trip, view participating properties, and select your dates.
            </p>
          </div>
        </div>
      </section>

      {/* ===== FIRST-LOOK SOURCING AGREEMENT TEXT PANEL ===== */}
      <section className="py-24 lg:py-28 bg-[#020617]" data-testid="first-look-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">OUR B2B FIRST-LOOK SOURCING AGREEMENT</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 tracking-tighter">
            Why is this annual corporate license <span className="text-red-500">only $485?</span> <br />What is the catch?
          </h2>
          <div className="mt-8 space-y-5 text-slate-300 text-base md:text-lg leading-relaxed">
            <p>
              Let&apos;s cut through the noise. <span className="text-white font-semibold">We do not make money on hotel rooms.</span> We are a global wholesale sourcing and procurement consulting firm. We use our bulk corporate purchasing power to snap up unallocated, empty luxury resort room inventory for pennies on the dollar and pass that utility to your business at our exact cost.
            </p>
            <p>
              In exchange for accessing this high-volume voucher portal, you contractually agree to grant our firm a <span className="text-red-400 font-semibold">non-exclusive First Right of Refusal</span> to review and submit competitive bids on your internal logistics, property maintenance materials, or supply chain needs. If our network cannot beat your current supplier&apos;s pricing or quality, <span className="text-white font-semibold">you are 100% free to keep using them</span>. You retain your unlimited luxury travel portal either way. It is a zero-risk win for your bottom line.
            </p>
          </div>
          <div className="mt-10">
            <Link to="/activate" data-testid="first-look-cta" className="aios-btn-primary text-sm aios-pulse">
              Review the Pricing Matrix
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== STILL IN DISBELIEF · $9.95 TEST DRIVE DOWNSELL ===== */}
      <section className="relative py-24 lg:py-32 bg-[#020617] border-t border-slate-900 overflow-hidden" data-testid="disbelief-section">
        <div className="absolute inset-0 aios-starfield opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/40 via-transparent to-[#020617]" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold uppercase">
            Still In Disbelief?
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mt-4 tracking-tighter leading-[1.05]">
            Still in disbelief? Try a <span className="text-red-500">3-night luxury hotel</span> for only <span className="text-red-500">$9.95</span> &mdash; including tax.
          </h2>
          <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-3xl mx-auto">
            No contract. No timeshare pitch. No catch buried in fine print. Pay $9.95 once, activate a single-use 3-night voucher at one of our luxury hotel partners, and see exactly how our sourcing network operates &mdash; before you ever commit to the full $485 portal.
          </p>
          <div className="mt-10 inline-flex flex-col items-center gap-3">
            <Link
              to="/test-drive"
              data-testid="disbelief-cta-testdrive"
              className="aios-btn-primary text-base aios-pulse"
            >
              <Ticket className="w-5 h-5" />
              [ GET MY $9.95 VOUCHER NOW ]
              <ArrowRight className="w-5 h-5" />
            </Link>
            <span className="text-xs text-slate-500 font-display tracking-widest uppercase">
              Tax included &middot; One-time payment &middot; No recurring billing
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
